// The calendar grids of Appointments (design/screens/appointments.html `.cal`; UC-APPOINTMENTS-007): the day by staff column
// in half-hour slots, and the week by staff row and day column. Columns are the branch's mapped staff in mapping order plus an
// Unassigned column when an appointment has no staff member (A2). Cancelled and rescheduled appointments are not booked
// (BR-APPOINTMENTS-011) and stay off the grid. Blocks carry the status as short text — the one place a status is not a chip.
import type { AppointmentView, CalendarView, UserRef } from "@visionx/contracts";
import { formatTime } from "../lib/format";
import { STATUS_WORD, hm, minutesOfDay, typeClass } from "./AppointmentsBits";

export const SLOT_MINUTES = 30;
/**
 * The narrowest a day's staff column and a week's day column may be (UC-APPOINTMENTS-007 A6): below it the calendar scrolls
 * sideways inside its own frame (`#s-appointments .cal`, the time column staying in view) rather than squeezing a name and its
 * blocks to a sliver — about 15 px each on a 768 px tablet before the testing of 12 Oct 2026 — or making the page scroll.
 */
export const STAFF_COLUMN_MIN_PX = 120;
export const DAY_COLUMN_MIN_PX = 112;
const DAY_START = 8 * 60;
const DAY_END = 17 * 60;
const UNASSIGNED = "__unassigned__";

export interface Column { id: string; name: string; sub: string | null }

/** The staff columns of a view: mapping order, then Unassigned when needed. */
export function columnsOf(view: CalendarView): Column[] {
  const cols: Column[] = view.staff.map((u: UserRef) => ({ id: u.id, name: u.displayName, sub: null }));
  if (view.appointments.some((a) => a.optometristUserId === null && onGrid(a))) cols.push({ id: UNASSIGNED, name: "Unassigned", sub: "no staff member mapped" });
  for (const a of view.appointments) {
    if (a.optometristUserId && !cols.some((c) => c.id === a.optometristUserId) && onGrid(a)) cols.push({ id: a.optometristUserId, name: a.optometrist?.displayName ?? "Staff", sub: null });
  }
  return cols;
}
export const onGrid = (a: AppointmentView): boolean => a.status !== "Cancelled" && a.status !== "Rescheduled";
const columnOf = (a: AppointmentView): string => a.optometristUserId ?? UNASSIGNED;

/** The half-hour rows the day needs: 08:00–17:00, stretched to cover an early or late appointment. */
export function slotsOf(appts: AppointmentView[]): number[] {
  let start = DAY_START;
  let end = DAY_END;
  for (const a of appts) {
    if (!onGrid(a)) continue;
    const s = minutesOfDay(a.startAt);
    const e = s + a.durationMinutes;
    start = Math.min(start, Math.floor(s / SLOT_MINUTES) * SLOT_MINUTES);
    end = Math.max(end, Math.ceil(e / SLOT_MINUTES) * SLOT_MINUTES);
  }
  const out: number[] = [];
  for (let m = start; m < end; m += SLOT_MINUTES) out.push(m);
  return out;
}

function Block({ a, selected, onSelect, compact }: { a: AppointmentView; selected: boolean; onSelect: (a: AppointmentView) => void; compact?: boolean | undefined }) {
  const blocks = Math.max(1, Math.ceil(a.durationMinutes / SLOT_MINUTES));
  const style = compact ? undefined : blocks > 1 ? { height: `calc(${blocks * 100}% + ${(blocks - 1) * 2}px)`, zIndex: 1 } : undefined;
  const text = compact ? `${formatTime(a.startAt)} · ${a.appointmentType.name}` : `${a.appointmentType.name}${a.durationMinutes !== SLOT_MINUTES ? ` · ${a.durationMinutes} min` : ""} · ${STATUS_WORD[a.status]}`;
  return (
    <button type="button" className={`appt ${typeClass(a.appointmentType.code)}${selected ? " on" : ""}${a.warnings.length > 0 ? " warn" : ""}`} style={style} aria-pressed={selected} aria-label={`${formatTime(a.startAt)} ${a.patient.displayName}, ${a.appointmentType.name}, ${STATUS_WORD[a.status]}`} onClick={() => onSelect(a)}>
      <b>{a.patient.displayName}</b>
      <span>{text}{a.warnings.length > 0 ? ` · ${a.warnings.some((w) => w.code === "patient_dormant") ? "Dormant" : "Prohibited"}` : ""}</span>
      {!compact && blocks > 1 && <span className="mono">{a.patient.accountCode}</span>}
    </button>
  );
}

export function DayGrid({ view, selectedId, onSelect, nowMinutes }: { view: CalendarView; selectedId: string | null; onSelect: (a: AppointmentView) => void; nowMinutes: number | null }) {
  const cols = columnsOf(view);
  const appts = view.appointments.filter(onGrid);
  const slots = slotsOf(appts);
  const at = (colId: string, slot: number) => appts.filter((a) => columnOf(a) === colId && Math.floor(minutesOfDay(a.startAt) / SLOT_MINUTES) * SLOT_MINUTES === slot);
  return (
    <div className="cal" style={{ gridTemplateColumns: `64px repeat(${Math.max(cols.length, 1)}, minmax(${STAFF_COLUMN_MIN_PX}px, 1fr))` }} role="grid" aria-label="Day calendar">
      <div className="hd"><span className="sub">Time</span></div>
      {cols.map((c, i) => <div key={c.id} className={`hd${i === cols.length - 1 ? " last" : ""}`} role="columnheader">{c.name}{c.sub && <span className="sub">{c.sub}</span>}</div>)}
      {cols.length === 0 && <div className="hd last" role="columnheader">No staff mapped<span className="sub">Settings › Appointments</span></div>}
      {slots.map((slot) => {
        const nowHere = nowMinutes !== null && nowMinutes >= slot && nowMinutes < slot + SLOT_MINUTES;
        const nowTop = nowHere ? `${((nowMinutes! - slot) / SLOT_MINUTES) * 100}%` : undefined;
        return [
          <div key={`t${slot}`} className="tm">{hm(slot)}</div>,
          ...(cols.length === 0 ? [<div key={`e${slot}`} className="slot last" />] : cols.map((c, i) => {
            const here = at(c.id, slot);
            return (
              <div key={`${c.id}${slot}`} className={`slot${i === cols.length - 1 ? " last" : ""}`} role="gridcell">
                {here.length <= 1 ? here.map((a) => <Block key={a.id} a={a} selected={a.id === selectedId} onSelect={onSelect} />) : (
                  <div className="slot-many">{here.map((a) => <Block key={a.id} a={a} selected={a.id === selectedId} onSelect={onSelect} compact />)}</div>
                )}
                {nowHere && <i className="now" style={{ top: nowTop }} aria-hidden="true" />}
              </div>
            );
          })),
        ];
      })}
    </div>
  );
}

/** Six days by staff row: each cell lists the day's blocks in start order. */
export function WeekGrid({ view, days, selectedId, onSelect, dayLabel }: { view: CalendarView; days: string[]; selectedId: string | null; onSelect: (a: AppointmentView) => void; dayLabel: (date: string) => string }) {
  const cols = columnsOf(view);
  const appts = view.appointments.filter(onGrid);
  const dayOf = (a: AppointmentView): string => localDateOfInstant(a.startAt);
  return (
    <div className="cal week" style={{ gridTemplateColumns: `140px repeat(${days.length}, minmax(${DAY_COLUMN_MIN_PX}px, 1fr))` }} role="grid" aria-label="Week calendar">
      <div className="hd"><span className="sub">Staff</span></div>
      {days.map((d, i) => <div key={d} className={`hd${i === days.length - 1 ? " last" : ""}`} role="columnheader">{dayLabel(d)}</div>)}
      {(cols.length === 0 ? [{ id: UNASSIGNED, name: "No staff mapped", sub: null }] : cols).map((c) => [
        <div key={`h${c.id}`} className="tm staff">{c.name}</div>,
        ...days.map((d, i) => (
          <div key={`${c.id}${d}`} className={`slot auto${i === days.length - 1 ? " last" : ""}`} role="gridcell">
            {appts.filter((a) => columnOf(a) === c.id && dayOf(a) === d).sort((a, b) => a.startAt.localeCompare(b.startAt)).map((a) => <Block key={a.id} a={a} selected={a.id === selectedId} onSelect={onSelect} compact />)}
          </div>
        )),
      ])}
    </div>
  );
}

/** The organisation-local calendar date of an instant, `YYYY-MM-DD`, through Intl. */
export function localDateOfInstant(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: currentTimezone(), year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}
let tz = "Africa/Johannesburg";
/** The calendar keeps its own copy of the organisation timezone; lib/format sets it from the session. */
export function setCalendarTimezone(t: string): void { tz = t; }
export const currentTimezone = (): string => tz;
